import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useNotify } from '@/api/notify';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { controlClass, Field } from '@/components/Field';
import { OneTimePasswordModal, type IssuedPassword } from '@/components/OneTimePasswordModal';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';

import {
  platformKeys,
  provisionCompany,
  setBillingTerms,
  usePlans,
  type ProvisionInput,
} from './api';
import { newTermsDraft, termsProblems, termsRequest, todayIso, type TermsDraft } from './billing';
import { moduleLabels } from './modules';
import { provisionProblems } from './provision';
import { TermsFields } from './TermsFields';

const blank: ProvisionInput = {
  code: '',
  name: '',
  legalName: '',
  gstin: '',
  pan: '',
  contactEmail: '',
  contactPhone: '',
  ownerUsername: '',
  ownerMobile: '',
  ownerFullName: '',
  ownerEmail: '',
  planCode: '',
  startTrial: false,
};

const trimmed = (value: string | undefined) => (value?.trim() ? value.trim() : undefined);

export function NewCompanyPage({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (tenantId: number) => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const plans = usePlans();
  const fields = useAutoFocusFirstField<HTMLDivElement>();
  const [input, setInput] = useState<ProvisionInput>(blank);
  const [terms, setTerms] = useState<TermsDraft>(() => newTermsDraft(''));
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<(IssuedPassword & { tenantId: number }) | null>(null);

  // The cheapest package as the default, which is also the honest starting point for a pilot. The
  // package lives on the terms draft, which the terms fields edit; provisioning reads it from there.
  useEffect(() => {
    if (!terms.planCode && plans.data?.length)
      setTerms((t) => ({ ...t, planCode: plans.data[0]!.code }));
  }, [plans.data, terms.planCode]);

  const plan = plans.data?.find((p) => p.code === terms.planCode);
  const problems = [
    ...provisionProblems({ ...input, planCode: terms.planCode }),
    ...termsProblems(terms, plan, todayIso()),
  ];

  const submit = async () => {
    setBusy(true);
    try {
      const created = await provisionCompany({
        ...input,
        planCode: terms.planCode,
        // The terms decide this now: a pilot is a trial with a date somebody chose.
        startTrial: false,
        code: input.code.trim().toUpperCase(),
        name: input.name.trim(),
        legalName: trimmed(input.legalName),
        gstin: trimmed(input.gstin),
        pan: trimmed(input.pan),
        contactEmail: trimmed(input.contactEmail),
        contactPhone: trimmed(input.contactPhone),
        ownerEmail: trimmed(input.ownerEmail),
        ownerUsername: input.ownerUsername.trim(),
        ownerFullName: input.ownerFullName.trim(),
      });

      // A second call rather than part of provisioning, so terms have one writer on the server. If
      // it fails the company still exists — "No terms set" on its page, with the button to fix it —
      // and the owner's one-time password must still be shown, since it cannot be shown again.
      try {
        await setBillingTerms(created.tenantId, termsRequest(terms));
      } catch (error) {
        notify.error(error);
      }

      await queryClient.invalidateQueries({ queryKey: platformKeys.companies });
      setIssued({
        tenantId: created.tenantId,
        username: created.owner.username,
        password: created.temporaryPassword,
      });
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  const text = (
    key: keyof ProvisionInput,
    label: string,
    options: { hint?: string; required?: boolean; type?: string; mono?: boolean } = {},
  ) => (
    <Field label={label} hint={options.hint} required={options.required ?? false}>
      <input
        className={`${controlClass} ${options.mono ? 'font-mono' : ''}`}
        type={options.type ?? 'text'}
        value={String(input[key] ?? '')}
        onChange={(event) => setInput((i) => ({ ...i, [key]: event.target.value }))}
      />
    </Field>
  );

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Back to companies"
          className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
        >
          <i className="icon icon-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">New company</h1>
          <p className="page-subtitle">
            Creates the company, its package, its roles and its owner. Branches, the year, numbering
            and the catalogue come next, on the company&apos;s own page.
          </p>
        </div>
      </div>

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 flex flex-col gap-4 xl:col-span-8">
          <Card>
            <SectionTitle icon="icon icon-building-2" hue="indigo">
              The customer
            </SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {text('code', 'Company code', {
                hint: 'Letters and digits; staff type it to sign in. Cannot be changed later.',
                required: true,
                mono: true,
              })}
              {text('name', 'Company name', { hint: 'What the shop is called.', required: true })}
              {text('legalName', 'Legal name', { hint: 'As registered, if different.' })}
              {text('gstin', 'GSTIN', {
                hint: 'Leave blank for an unregistered shop — it issues bills of supply.',
                mono: true,
              })}
              {text('pan', 'PAN', { mono: true })}
              {text('contactPhone', 'Contact phone', { type: 'tel' })}
              {text('contactEmail', 'Contact email', { type: 'email' })}
            </div>
          </Card>

          <Card>
            <SectionTitle icon="icon icon-user-round" hue="teal">
              The owner
            </SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {text('ownerFullName', 'Full name', { required: true })}
              {text('ownerUsername', 'Username', {
                hint: 'May not begin with a digit.',
                required: true,
              })}
              {text('ownerMobile', 'Mobile', {
                hint: 'Also a sign-in name — unique within the company.',
                required: true,
                type: 'tel',
              })}
              {text('ownerEmail', 'Email', { type: 'email' })}
            </div>
          </Card>

          <TermsFields
            draft={terms}
            onChange={setTerms}
            plans={plans.data ?? []}
            packageFooter={
              plan && (
                <p className="text-default mt-3 text-[length:var(--scale-field-hint)]">
                  Includes: {plan.features.map((f) => moduleLabels[f]).join(', ')}. Modules can be
                  switched on or off for this company afterwards.
                </p>
              )
            }
          />
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          {problems.length > 0 && (
            <Notice tone="warning" title="Still needed">
              <ul className="ps-0">
                {problems.map((p) => (
                  <li key={p} className="list-none">
                    {p}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          <Card>
            <p className="text-default mb-3 text-[length:var(--scale-field-hint)]">
              The owner gets a one-time password, shown once here; they choose their own at first
              sign-in.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                disabled={busy || problems.length > 0}
                onClick={() => void submit()}
              >
                {busy ? 'Creating…' : 'Create company'}
              </Button>
              <Button disabled={busy} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <OneTimePasswordModal
        issued={issued}
        onClose={() => {
          const tenantId = issued?.tenantId;
          setIssued(null);
          if (tenantId) onCreated(tenantId);
        }}
      />
    </>
  );
}
